'use client';

import React, { useEffect, useRef, useState } from 'react';

interface StatItem {
  value: string;
  label: string;
  sublabel: string;
  target: number;
  prefix?: string;
  suffix?: string;
  extra?: string;
}

const statsData: StatItem[] = [
  {
    value: '80+',
    label: 'Apps & Websites Delivered',
    sublabel: 'Across industries and platforms',
    target: 80,
    suffix: '+',
  },
  {
    value: '₹50Cr+',
    label: 'Client Revenue Generated',
    sublabel: 'Via apps, web & paid ads',
    target: 50,
    prefix: '₹',
    suffix: 'Cr+',
  },
  {
    value: '94%',
    label: 'Client Retention',
    sublabel: 'Clients who stay and scale',
    target: 94,
    suffix: '%',
  },
  {
    value: '4.9×',
    label: 'Average ROAS',
    sublabel: 'Meta + TikTok + Google Ads',
    target: 4,
    extra: '.9×',
  },
];

const badges = [
  { emoji: '📱', label: 'App Development' },
  { emoji: '🌐', label: 'Google Ads Certified' },
  { emoji: '📘', label: 'Meta Business Partner' },
  { emoji: '🎵', label: 'TikTok Ads Expert' },
  { emoji: '🔒', label: 'NDA Protected' },
  { emoji: '🇮🇳', label: 'Made in India' },
];

export default function StatsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [animated, setAnimated] = useState(false);
  const [counts, setCounts] = useState([0, 0, 0, 0]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            if (!animated) {
              setAnimated(true);
              animateCounts();
            }
          }
        });
      },
      { threshold: 0.1 }
    );

    const reveals = sectionRef.current?.querySelectorAll('.reveal');
    reveals?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [animated]);

  const animateCounts = () => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const eased = 1 - Math.pow(1 - progress, 3);

      setCounts([
        Math.floor(eased * 80),
        Math.floor(eased * 50),
        Math.floor(eased * 94),
        parseFloat((eased * 4.9).toFixed(1)),
      ]);

      if (step >= steps) clearInterval(timer);
    }, interval);
  };

  const formatStat = (index: number) => {
    const val = counts[index];
    switch (index) {
      case 0: return `${val}+`;
      case 1: return `₹${val}Cr+`;
      case 2: return `${val}%`;
      case 3: return `${val}×`;
      default: return `${val}`;
    }
  };

  return (
    <section ref={sectionRef} className="py-24 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full"
          style={{
            background: 'radial-gradient(ellipse, rgba(245,166,35,0.06) 0%, transparent 70%)',
            filter: 'blur(60px)',
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-16 reveal">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-accent/20 bg-accent/5 mb-6">
            <span className="text-xs font-700 text-accent tracking-widest uppercase">Our Track Record</span>
          </div>
          <h2
            className="font-display font-800 text-foreground tracking-tight mb-4"
            style={{
              fontFamily: '"DM Sans", sans-serif',
              fontWeight: 800,
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              lineHeight: 1.15,
            }}
          >
            Numbers That{' '}
            <span className="text-gradient-amber">Speak</span>
          </h2>
          <p className="text-muted font-400 leading-relaxed">
            Since 2022, we&apos;ve helped businesses build apps and web products that generate consistent revenue through expert development and high-performance paid advertising.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
          {statsData.map((stat, i) => (
            <div
              key={stat.label}
              className={`glass-card glass-card-hover rounded-3xl p-8 text-center reveal reveal-delay-${i + 1}`}
            >
              <div
                className="text-4xl md:text-5xl font-800 font-display text-accent mb-2"
                style={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 800 }}
              >
                {animated ? formatStat(i) : stat.value}
              </div>
              <div className="text-base font-700 text-foreground mb-1">{stat.label}</div>
              <div className="text-sm text-muted">{stat.sublabel}</div>
            </div>
          ))}
        </div>

        {/* Badges */}
        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 reveal">
          {badges.map((badge) => (
            <div
              key={badge.label}
              className="flex items-center gap-2 px-4 py-2 glass-card rounded-full text-sm font-500 text-muted"
            >
              <span>{badge.emoji}</span>
              <span>{badge.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
